import { createTheme, Theme } from '@mui/material/styles';

const buildTheme = (compact: boolean): Theme =>
  createTheme({
    palette: {
      background: {
        default: '#ffffff',
      },
      primary: {
        main: '#2e453d',
        light: '#ffd1c0',
      },
      secondary: {
        main: '#CBBCA2',
      },
      warning: {
        main: '#FF4500',
      },
      success: {
        main: '#008000',
      },
      error: {
        main: '#DC143C',
      },
      info: {
        main: '#708090',
      },
    },
    spacing: compact ? 6.4 : 8,
    typography: compact
      ? {
          fontSize: 11.2,
        }
      : undefined,
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
      },
    },
    components: {
      MuiFormControl: {
        styleOverrides: {
          root: {
            '& .Mui-disabled': {
              color: '#000',
              opacity: 1,
              WebkitTextFillColor: '#000 !important',
            },
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          root: {
            '& .MuiIconButton-root': {
              color: 'inherit',
            },
            '& .MuiInputAdornment-root': {
              color: 'inherit',
            },
            '& .MuiInputLabel-root': {
              color: 'inherit',
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: 'inherit',
            },
            '& .MuiOutlinedInput-root': {
              color: 'inherit',
            },
            '& .MuiOutlinedInput-root.Mui-focused': {
              '& > fieldset': {
                borderColor: 'inherit',
              },
            },
            '& .MuiOutlinedInput-root:hover': {
              '& > fieldset': {
                borderColor: 'inherit',
              },
            },
            '& .Mui-disabled': {
              color: '#000',
              opacity: 1,
            },
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          h1: {
            fontSize: compact ? '2rem' : '2.5rem',
            '@media (max-width: 600px)': {
              fontSize: compact ? '1.2rem' : '1.5rem',
            },
          },
          h2: {
            fontSize: compact ? '1.2rem' : '1.5rem',
            '@media (max-width:600px)': {
              fontSize: compact ? '0.8rem' : '1rem',
            },
          },
          h3: {
            fontSize: compact ? '0.8rem' : '1rem',
            '@media (max-width:600px)': {
              fontSize: compact ? '0.56rem' : '0.7rem',
            },
          },
          h4: {
            fontSize: compact ? '0.8rem' : '1rem',
            '@media (max-width:600px)': {
              fontSize: compact ? '1.2rem' : '1.5rem',
            },
          },
          h5: {
            fontSize: compact ? '1.32rem' : '1.65rem',
            '@media (max-width:600px)': {
              fontSize: compact ? '1.04rem' : '1.3rem',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          contained: {
            padding: compact ? '5px 7px' : '6px 9px',
            '@media (max-width:600px)': {
              fontSize: '0.5rem',
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            backgroundColor: '#2e453d',
            color: '#fff',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            color: '#fff',
            '&.Mui-selected': {
              color: '#ffd1c0',
            },
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          asterisk: {
            color: 'red',
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            '@media (max-width:600px)': {
              fontSize: '0.5rem',
            },
          },
        },
      },
    },
  });

// Used directly (e.g. `theme.palette.primary.main`) by many components outside
// of the ThemeProvider's render-time context, so it must stay a stable export.
// Its palette/breakpoints are identical between the two variants below — only
// spacing/typography differ, and those are picked responsively in App.tsx.
export const theme = buildTheme(false);

// ~20% smaller spacing/typography, applied only under 1500px width (see App.tsx).
export const compactTheme = buildTheme(true);
